import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const TACTICAL_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4dcdf432-b217-4bef-8d71-c057194f5698/images/545779a0df725f446dc4f8f6e127c9cbfdb90300827eb85791afec627913e39b.png";

export default function AuthPage({ mode = "login" }) {
  const isRegister = mode === "register";
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [career, setCareer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/dashboard";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = isRegister
      ? await register({ email, password, name, career })
      : await login(email, password);
    setLoading(false);
    if (result.ok) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-bone" data-testid="auth-page">
      {/* Left panel: image */}
      <div
        className="hidden lg:flex lg:col-span-3 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(5,26,15,0.55), rgba(5,26,15,0.85)), url(${TACTICAL_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 tactical-grid-dark" />
        <div className="relative z-10 flex flex-col justify-between p-16 text-bone w-full">
          <div className="flex items-center gap-3">
            <img
              src="/inaf-logo-white.png"
              alt="INAF"
              className="h-12 w-auto"
              data-testid="auth-brand-logo"
            />
          </div>

          <div className="max-w-xl">
            <div className="font-mono text-xs tracking-[0.3em] text-accent uppercase mb-5">
              Asistente metodológico · IA
            </div>
            <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] tracking-tight text-bone mb-6">
              Investigación deportiva con <em className="text-accent not-italic">rigor</em> y acompañamiento.
            </h1>
            <p className="font-sans text-base text-neutral-200 leading-relaxed max-w-lg">
              Guía tu trabajo académico desde la idea inicial hasta el informe final, en diez
              etapas metodológicas aplicadas al fútbol y al deporte.
            </p>
          </div>

          <div className="flex items-center gap-8 text-xs text-neutral-300 font-mono uppercase tracking-widest">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" /> Metodología
            </span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" /> Rigor académico
            </span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" /> Fútbol
            </span>
          </div>
        </div>
      </div>

      {/* Right panel: form */}
      <div className="col-span-1 lg:col-span-2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md fade-slide-up">
          <div className="mb-8">
            <div className="font-mono text-xs tracking-[0.28em] uppercase text-accent-dark mb-3">
              {isRegister ? "Registro de estudiante" : "Iniciar sesión"}
            </div>
            <h2 className="font-serif text-4xl text-primary-800 leading-tight">
              {isRegister ? "Crea tu cuenta" : "Bienvenido de vuelta"}
            </h2>
            <p className="text-sm text-neutral-600 mt-2">
              {isRegister
                ? "Comienza a estructurar tu investigación deportiva."
                : "Accede a tus proyectos de investigación."}
            </p>
          </div>

          {error && (
            <div
              className="mb-4 px-4 py-3 border border-destructive/30 bg-destructive/5 text-destructive text-sm rounded-sm font-sans"
              data-testid="auth-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <>
                <Field label="Nombre completo">
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent transition"
                    placeholder="Ej. Juan Pérez"
                    data-testid="auth-name-input"
                  />
                </Field>
                <Field label="Carrera o programa">
                  <select
                    value={career}
                    onChange={(e) => setCareer(e.target.value)}
                    className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent transition"
                    data-testid="auth-career-select"
                  >
                    <option value="">Selecciona una carrera</option>
                    <option>Entrenador de Fútbol</option>
                    <option>Técnico de Fútbol</option>
                    <option>Analítica Deportiva</option>
                    <option>Gestión Deportiva</option>
                    <option>Preparación Física</option>
                    <option>Otra</option>
                  </select>
                </Field>
              </>
            )}

            <Field label="Correo institucional">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent transition"
                placeholder="estudiante@inaf.cl"
                data-testid="auth-email-input"
              />
            </Field>

            <Field label="Contraseña">
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent transition"
                placeholder={isRegister ? "Mínimo 6 caracteres" : "••••••••"}
                data-testid="auth-password-input"
              />
            </Field>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-800 text-bone font-sans font-medium py-3 rounded-sm hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed tracking-wide"
              data-testid="auth-submit-button"
            >
              {loading
                ? "Procesando..."
                : isRegister
                ? "Crear cuenta"
                : "Ingresar"}
            </button>
          </form>

          <div className="mt-6 text-sm text-neutral-600 font-sans">
            {isRegister ? (
              <>
                ¿Ya tienes cuenta?{" "}
                <Link
                  to="/login"
                  className="text-accent-dark hover:text-accent font-medium underline underline-offset-4"
                  data-testid="link-to-login"
                >
                  Inicia sesión
                </Link>
              </>
            ) : (
              <>
                ¿Primera vez aquí?{" "}
                <Link
                  to="/register"
                  className="text-accent-dark hover:text-accent font-medium underline underline-offset-4"
                  data-testid="link-to-register"
                >
                  Regístrate
                </Link>
              </>
            )}
          </div>

          <div className="mt-10 pt-6 border-t border-border text-xs text-neutral-600 font-mono tracking-wider uppercase">
            INAF · Investigación Deportiva · 2026
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-600 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
